/**
 * FASE 2 · Jun 2026 — Dashboard Financiero inicial (solo Admin, solo lectura).
 * Consume GET /api/finance/summary (Fase 1). Sin gráficos ni comparaciones.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/context/I18nContext";
import { ReceivablesDonut, TrendChart } from "@/pages/panels/FinanceCharts";
import { ReceivablesTable } from "@/pages/panels/ReceivablesTable";
import { BankImport } from "@/pages/panels/BankImport";
import { ExpenseSummary } from "@/pages/panels/ExpenseSummary";
import { BankTransactions } from "@/pages/panels/BankTransactions";
import ReceiptsPanel from "@/pages/panels/ReceiptsPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const PERIODS = ["today", "week", "month", "year", "custom"];

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);
const pct = (v) => `${Number(v || 0).toFixed(2).replace(/\.00$/, "")}%`;

// Presentación visual: convierte el fin EXCLUSIVO del backend en último día INCLUIDO.
// No modifica las fechas enviadas al API.
const parseYmd = (iso) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const fmtRange = (startIso, endIso, lang) => {
  if (!startIso || !endIso) return "";
  const s = parseYmd(startIso);
  const e = parseYmd(endIso);
  e.setUTCDate(e.getUTCDate() - 1); // fin exclusivo → último día incluido
  const locale = lang === "en" ? "en-US" : "es";
  const fmt = (d, withYear) =>
    new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      ...(withYear ? { year: "numeric" } : {}),
      timeZone: "UTC",
    }).format(d);
  const sameYear = s.getUTCFullYear() === e.getUTCFullYear();
  return `${fmt(s, !sameYear)} – ${fmt(e, true)}`;
};

const marginOf = (d) =>
  d && d.net_sales_before_tax > 0 ? (d.provisional_gross_profit_before_tax / d.net_sales_before_tax) * 100 : 0;

// Fila de comparación: verde/rojo según regla del KPI (inverse=true para costos).
export function DeltaRow({ id, label, cur, prev, inverse, unit }) {
  const { t } = useI18n();
  const c = Number(cur || 0);
  const p = Number(prev || 0);
  const diff = c - p;
  const dir = diff > 0.005 ? "up" : diff < -0.005 ? "down" : "flat";
  const good = dir === "flat" ? null : inverse ? dir === "down" : dir === "up";
  const color =
    dir === "flat" ? "text-[var(--text-secondary)]" : good ? "text-[#0F7B3E]" : "text-[#C5221F]";
  const arrow = dir === "up" ? "▲" : dir === "down" ? "▼" : "—";
  const moneyAbs = money(Math.abs(diff));
  const sign = diff >= 0 ? "+" : "−";
  let txt;
  if (p === 0 && c === 0) txt = t("fin_no_change");
  else if (p === 0)
    txt = `${t("fin_new")} · ${unit === "pp" ? `${c >= 0 ? "+" : ""}${c.toFixed(1)} pp` : `${c >= 0 ? "+" : ""}${money(c)}`}`;
  else if (unit === "pp") txt = `${sign}${Math.abs(diff).toFixed(1)} pp`;
  else txt = `${sign}${moneyAbs} (${sign}${Math.abs((diff / Math.abs(p)) * 100).toFixed(1)}%)`;
  return (
    <div data-testid={id} className={`text-[11px] font-mono-tech leading-tight ${color}`}>
      {arrow} {txt} <span className="text-[var(--text-secondary)]">· {label}</span>
    </div>
  );
}

export default function FinancePanel({ onReviewPendingCost }) {
  const { t, lang } = useI18n();
  const [view, setView] = useState("resumen"); // "resumen" | "recibos"
  const [period, setPeriod] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const downloadReport = async (fmt) => {
    if (period === "custom" && (!start || !end)) return;
    setDownloading(true);
    try {
      const params = { period };
      if (period === "custom") { params.start = start; params.end = end; }
      const res = await axios.get(`${API}/api/finance/report/${fmt}`, { params, responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename="(.+)"/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `Rawens_Finanzas.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(t("fin_err_server"));
    } finally {
      setDownloading(false);
    }
  };

  const load = useCallback(async (p, s, e) => {
    setLoading(true);
    setError("");
    try {
      const params = { period: p };
      if (p === "custom") {
        params.start = s;
        params.end = e;
      }
      const [cRes, tsRes] = await Promise.all([
        axios.get(`${API}/api/finance/compare`, { params }),
        axios.get(`${API}/api/finance/timeseries`, { params }),
      ]);
      setData(cRes.data.current);
      setCmp(cRes.data);
      setSeries(tsRes.data);
    } catch (err) {
      setData(null);
      setCmp(null);
      setSeries(null);
      const st = err?.response?.status;
      setError(
        st === 401 || st === 403
          ? t("fin_err_forbidden")
          : err?.response?.data?.detail || t("fin_err_server")
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (period !== "custom") load(period);
  }, [period, load]);

  const prevD = cmp?.previous;
  const yoyD = cmp?.yoy;
  const kpis = data
    ? [
        { k: "fin_kpi_sales_pretax", v: money(data.net_sales_before_tax), id: "fin-kpi-sales-pretax",
          cmp: { cur: data.net_sales_before_tax, prev: prevD?.net_sales_before_tax, yoy: yoyD?.net_sales_before_tax } },
        { k: "fin_kpi_tax_invoiced", v: money(data.sales_tax_invoiced), id: "fin-kpi-tax-invoiced",
          cmp: { cur: data.sales_tax_invoiced, prev: prevD?.sales_tax_invoiced, yoy: yoyD?.sales_tax_invoiced } },
        { k: "fin_kpi_total_with_tax", v: money(data.total_invoiced_with_tax), id: "fin-kpi-invoiced",
          cmp: { cur: data.total_invoiced_with_tax, prev: prevD?.total_invoiced_with_tax, yoy: yoyD?.total_invoiced_with_tax } },
        { k: "fin_kpi_exempt", v: money(data.tax_exempt_sales), id: "fin-kpi-exempt" },
        { k: "fin_kpi_collected", v: money(data.collected_in_period), id: "fin-kpi-collected", sub: "fin_collected_note",
          cmp: { cur: data.collected_in_period, prev: prevD?.collected_in_period, yoy: yoyD?.collected_in_period } },
        { k: "fin_kpi_receivables", v: money(data.current_receivables), id: "fin-kpi-receivables", note: true },
        { k: "fin_kpi_costs", v: money(data.known_costs_total), id: "fin-kpi-costs",
          cmp: { cur: data.known_costs_total, prev: prevD?.known_costs_total, yoy: yoyD?.known_costs_total, inverse: true } },
        { k: "fin_kpi_gross_pretax", v: money(data.provisional_gross_profit_before_tax), id: "fin-kpi-gross", est: !data.profit_complete,
          cmp: { cur: data.provisional_gross_profit_before_tax, prev: prevD?.provisional_gross_profit_before_tax, yoy: yoyD?.provisional_gross_profit_before_tax } },
        { k: "fin_kpi_margin_pretax", v: pct(data.provisional_gross_margin_before_tax), id: "fin-kpi-margin", est: !data.profit_complete,
          cmp: { cur: data.provisional_gross_margin_before_tax, prev: marginOf(prevD), yoy: marginOf(yoyD), unit: "pp" } },
        { k: "fin_kpi_coverage", v: data.total_line_count === 0 ? "N/A" : pct(data.cost_coverage_pct), id: "fin-kpi-coverage" },
      ]
    : [];

  return (
    <div data-testid="finance-panel" className="space-y-6">
      <div>
        <div className="label-overline">GP / ADMIN</div>
        <h2 className="font-display text-3xl font-black tracking-tight">{t("fin_title")}</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{t("fin_subtitle")}</p>
      </div>

      {/* SPRINT RECIBOS — sub-secciones de Contabilidad */}
      <div className="flex gap-1">
        <button
          data-testid="fin-view-resumen"
          onClick={() => setView("resumen")}
          className={`flex-1 sm:flex-none px-5 py-3 text-xs font-mono-tech font-bold border transition-colors ${
            view === "resumen" ? "bg-black text-white border-black" : "border-[var(--border)] hover:bg-[var(--surface-alt)]"
          }`}
        >
          {t("rc_tab_summary")}
        </button>
        <button
          data-testid="fin-view-recibos"
          onClick={() => setView("recibos")}
          className={`flex-1 sm:flex-none px-5 py-3 text-xs font-mono-tech font-bold border transition-colors ${
            view === "recibos" ? "bg-black text-white border-black" : "border-[var(--border)] hover:bg-[var(--surface-alt)]"
          }`}
        >
          {t("rc_tab_receipts")}
        </button>
      </div>

      {view === "recibos" ? (
        <ReceiptsPanel />
      ) : (
        <>
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button
            key={p}
            data-testid={`fin-period-${p}`}
            onClick={() => setPeriod(p)}
            className={`px-4 py-2 text-xs font-mono-tech font-bold border ${
              period === p ? "bg-black text-white border-black" : "border-[var(--border)] hover:bg-[var(--surface-alt)]"
            }`}
          >
            {t(`fin_period_${p}`)}
          </button>
        ))}
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <input data-testid="fin-custom-start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="border border-[var(--border)] px-2 py-1.5 text-sm font-mono-tech" />
            <span className="text-xs">→</span>
            <input data-testid="fin-custom-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="border border-[var(--border)] px-2 py-1.5 text-sm font-mono-tech" />
            <button data-testid="fin-custom-apply" onClick={() => start && end && load("custom", start, end)}
              className="px-4 py-2 text-xs font-mono-tech font-bold bg-black text-white border border-black">
              {t("fin_apply")}
            </button>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <button data-testid="fin-dl-pdf" onClick={() => downloadReport("pdf")} disabled={downloading}
            className="px-4 py-2 text-xs font-mono-tech font-bold border border-black bg-white hover:bg-black hover:text-white transition-colors disabled:opacity-40">
            {t("fin_dl_pdf")}
          </button>
          <button data-testid="fin-dl-xlsx" onClick={() => downloadReport("xlsx")} disabled={downloading}
            className="px-4 py-2 text-xs font-mono-tech font-bold border border-black bg-white hover:bg-black hover:text-white transition-colors disabled:opacity-40">
            {t("fin_dl_xlsx")}
          </button>
        </div>
      </div>

      {data && (
        <div data-testid="fin-range" className="text-xs font-mono-tech text-[var(--text-secondary)]">
          {fmtRange(data.start_local, data.end_local, lang)} · {data.timezone}
        </div>
      )}

      {loading && <div data-testid="fin-loading" className="text-sm font-mono-tech py-10">{t("fin_loading")}</div>}
      {!loading && error && (
        <div data-testid="fin-error" className="border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {data.invoice_count === 0 && data.payment_count === 0 && (
            <div data-testid="fin-empty" className="text-sm italic text-[var(--text-secondary)]">{t("fin_empty")}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((c) => (
              <div key={c.k} data-testid={c.id} className="border border-black bg-white p-5">
                <div className="label-overline">{t(c.k)}{c.est ? ` · ${t("fin_estimated")}` : ""}</div>
                <div className="font-display text-3xl font-black mt-2">{c.v}</div>
                {c.sub && <div className="mt-1 text-[10px] font-mono-tech text-[var(--text-secondary)]">{t(c.sub)}</div>}
                {c.cmp && cmp && (
                  <div className="mt-2 space-y-0.5">
                    <DeltaRow id={`${c.id}-vs-prev`} label={t("fin_vs_prev")} cur={c.cmp.cur} prev={c.cmp.prev} inverse={c.cmp.inverse} unit={c.cmp.unit} />
                    {!cmp.yoy_same_as_previous && (
                      <DeltaRow id={`${c.id}-vs-yoy`} label={t("fin_vs_yoy")} cur={c.cmp.cur} prev={c.cmp.yoy} inverse={c.cmp.inverse} unit={c.cmp.unit} />
                    )}
                  </div>
                )}
                {c.note && (
                  <div className="mt-2 text-[10px] font-mono-tech text-[var(--text-secondary)]">{t("fin_receivables_note")}</div>
                )}
              </div>
            ))}
          </div>

          {data.total_line_count === 0 ? (
            <div data-testid="fin-no-lines" className="border-l-4 border-[var(--border)] bg-[var(--surface-alt)] px-4 py-3 text-sm text-[var(--text-secondary)]">
              {t("fin_no_lines")}
            </div>
          ) : !data.profit_complete ? (
            <div data-testid="fin-cost-warning" className="border-l-4 border-[#B8860B] bg-[#FFF8E1] px-4 py-3 text-sm">
              <b>⚠️ {t("fin_warn_title")}:</b> {t("fin_pending_cost_alert", { n: data.lines_missing_cost })}
              <div className="font-mono-tech text-xs mt-2">
                {t("fin_lines_with_cost")}: {data.lines_with_cost} · {t("fin_lines_missing_cost")}: {data.lines_missing_cost} · {t("fin_kpi_coverage")}: {pct(data.cost_coverage_pct)}
              </div>
              {onReviewPendingCost && (
                <button
                  data-testid="fin-review-invoices-btn"
                  onClick={onReviewPendingCost}
                  className="mt-2 font-mono-tech text-xs font-bold border border-[#B8860B] px-3 py-1.5 hover:bg-[#B8860B] hover:text-white transition-colors"
                >
                  {t("fin_review_invoices")} →
                </button>
              )}
            </div>
          ) : (
            <div data-testid="fin-cost-complete" className="border-l-4 border-[#0F9D58] bg-[#F0FaF4] px-4 py-3 text-sm">
              ✅ {t("fin_costs_complete")}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ReceivablesDonut collected={data.collected_in_period} receivables={data.current_receivables} />
            <div className="lg:col-span-2">
              <TrendChart series={series?.series} granularity={series?.granularity} />
            </div>
          </div>

          <ExpenseSummary period={period} start={start} end={end} />

          <ReceivablesTable />

          <BankImport />

          <BankTransactions />

          <div data-testid="fin-secondary" className="border border-[var(--border)] bg-white p-4">
            <div className="label-overline mb-2">{t("fin_details")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 font-mono-tech text-sm">
              <div>{t("fin_invoice_count")}: <b>{data.invoice_count}</b></div>
              <div>{t("fin_payment_count")}: <b>{data.payment_count}</b></div>
              <div>{t("fin_total_lines")}: <b>{data.total_line_count}</b></div>
              <div>{t("fin_lines_with_cost")}: <b>{data.lines_with_cost}</b></div>
              <div>{t("fin_lines_missing_cost")}: <b>{data.lines_missing_cost}</b></div>
            </div>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
}
